import React, { createContext, useContext } from "react";
import { useTailwindBrand } from "../../../Components/shared";
import { DV2 } from "../../DeclarationV2/theme";

// ─── Style des sections ───────────────────────────────────────────────────────

/**
 * « legacy » : fiche client actuelle (titre dans la couleur de la marque,
 * icône dans une pastille). « v2 » : fiche repensée (maquette 4a), même carte
 * et mêmes titres que les autres blocs de la page, sans icône. Le contexte
 * évite de modifier les sections qui utilisent DetailSection.
 */
export type DetailSectionVariant = "legacy" | "v2";

export const DetailSectionStyleContext = createContext<DetailSectionVariant>("legacy");

// ─── Carte de section ─────────────────────────────────────────────────────────

interface DetailSectionProps {
    title: string;
    icon?: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Contenu aligné à droite du titre (bouton, lien). */
    action?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

/** Bloc titré de la fiche client, dans l'esprit des sections de DetailCours. */
export const DetailSection = ({ title, icon, subtitle, action, className = "", children }: DetailSectionProps) => {
    const { classes, brandConfig } = useTailwindBrand();
    const variant = useContext(DetailSectionStyleContext);
    if (variant === "v2") {
        return (
            <section
                className={`min-w-0 ${className}`}
                aria-label={title}
                style={{ background: DV2.surface, border: `1px solid ${DV2.border}`, borderRadius: DV2.radius, padding: 22, display: "flex", flexDirection: "column", gap: 12, fontFamily: brandConfig.fontFamily, color: brandConfig.colors.text }}
            >
                <div className="flex items-start justify-between gap-3">
                    <div style={{ minWidth: 0 }}>
                        <h3 style={{ fontWeight: 800, fontSize: 15, margin: 0, lineHeight: 1.3 }}>{title}</h3>
                        {subtitle && <div style={{ fontSize: 13, color: DV2.muted, marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    {action}
                </div>
                {children}
            </section>
        );
    }
    return (
        <section className={`bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-4 md:p-5 min-w-0 transition-shadow hover:shadow-md ${classes.fontFamily} ${className}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex items-start gap-3">
                    {icon && (
                        <span
                            className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-full ${classes.primaryText}`}
                            style={{ background: `${brandConfig.colors.primary}14` }}
                        >
                            {icon}
                        </span>
                    )}
                    <div className="min-w-0">
                        <h3 className={`${classes.primaryText} font-semibold text-base md:text-lg leading-tight`}>{title}</h3>
                        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                </div>
                {action}
            </div>
            {children}
        </section>
    );
};

// ─── État vide ────────────────────────────────────────────────────────────────

interface SectionEmptyProps {
    icon?: React.ReactNode;
    text: React.ReactNode;
    /** Bouton ou lien proposant l'action qui remplira la section. */
    action?: React.ReactNode;
}

/** État vide d'une section : une icône discrète, une phrase, une action éventuelle. */
export const SectionEmpty = ({ icon, text, action }: SectionEmptyProps) => (
    <div className="flex flex-col items-center text-center gap-2 py-5 px-2">
        {icon && <span className="text-gray-300 flex">{icon}</span>}
        <p className="text-sm text-gray-500 max-w-md">{text}</p>
        {action}
    </div>
);

// ─── Tuile chiffrée ───────────────────────────────────────────────────────────

export type StatTone = "default" | "success" | "warning" | "danger";

const TONE_STYLES: Record<StatTone, { wrap: string; value: string }> = {
    default: { wrap: "bg-gray-50 border-gray-100", value: "text-gray-900" },
    success: { wrap: "bg-emerald-50 border-emerald-100", value: "text-emerald-700" },
    warning: { wrap: "bg-amber-50 border-amber-200", value: "text-amber-700" },
    danger: { wrap: "bg-red-50 border-red-100", value: "text-red-600" },
};

interface StatTileProps {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    hint?: React.ReactNode;
    tone?: StatTone;
}

export const StatTile = ({ icon, label, value, hint, tone = "default" }: StatTileProps) => {
    const { classes } = useTailwindBrand();
    const s = TONE_STYLES[tone];
    return (
        <div className={`rounded-lg border p-3 min-w-0 ${s.wrap}`}>
            <div className="flex items-start gap-1.5 text-xs text-gray-500 mb-1">
                <span className={`${classes.primaryText} shrink-0 flex mt-px`}>{icon}</span>
                <span className="leading-tight break-words min-w-0">{label}</span>
            </div>
            <p className={`text-lg md:text-xl font-bold leading-tight break-words ${s.value}`}>{value}</p>
            {hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{hint}</p>}
        </div>
    );
};

// ─── Ligne libellé / valeur ───────────────────────────────────────────────────

interface InfoRowProps {
    label: string;
    value: React.ReactNode;
}

export const InfoRow = ({ label, value }: InfoRowProps) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 sm:gap-3 py-2 border-b border-gray-50 last:border-b-0">
        <span className="text-xs text-gray-500 shrink-0">{label}</span>
        <span className="text-sm font-medium text-gray-800 sm:text-right break-words min-w-0">{value}</span>
    </div>
);
